/* EBONFORM — shared behaviour for the ef-* sections. Everything degrades to plain, working HTML. */
(() => {
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Gentle reveal as sections enter the screen */
  const revealAll = (root) => {
    const items = root.querySelectorAll('.ef-reveal:not(.is-in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduce()) { items.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));
  };

  /* Image carousels inside cards: [data-ef-carousel] > [data-ef-track] > slides, [data-ef-dots] > i, [data-ef-prev]/[data-ef-next] */
  const carousels = (root) => {
    root.querySelectorAll('[data-ef-carousel]:not([data-ef-ready])').forEach((c) => {
      c.setAttribute('data-ef-ready', '');
      const track = c.querySelector('[data-ef-track]');
      if (!track) return;
      const slides = Array.from(track.children);
      const dots = Array.from(c.querySelectorAll('[data-ef-dots] > i'));
      const prev = c.querySelector('[data-ef-prev]');
      const next = c.querySelector('[data-ef-next]');
      let index = 0;
      const sync = () => {
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      const go = (i) => {
        const t = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: t * track.clientWidth, behavior: reduce() ? 'auto' : 'smooth' });
      };
      let tick = false;
      track.addEventListener('scroll', () => {
        if (tick) return; tick = true;
        requestAnimationFrame(() => {
          tick = false;
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== index) { index = Math.max(0, Math.min(slides.length - 1, i)); sync(); }
        });
      }, { passive: true });
      prev && prev.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); go(index - 1); });
      next && next.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); go(index + 1); });
      if ('ResizeObserver' in window) {
        let w = track.clientWidth;
        new ResizeObserver(() => { if (track.clientWidth !== w) { w = track.clientWidth; track.scrollLeft = index * w; } }).observe(track);
      }
      /* load the other views shortly before the card is reached */
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => es.forEach((e) => {
          if (!e.isIntersecting) return;
          c.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
          io.disconnect();
        }), { rootMargin: '400px 0px' });
        io.observe(c);
      }
      sync();
    });
  };

  /* Background video play / pause */
  const videos = (root) => {
    root.querySelectorAll('[data-ef-video-toggle]:not([data-ef-ready])').forEach((btn) => {
      btn.setAttribute('data-ef-ready', '');
      const video = btn.closest('[data-ef-video]')?.querySelector('video');
      if (!video) { btn.hidden = true; return; }
      if (reduce()) { video.pause(); video.removeAttribute('autoplay'); }
      const sync = () => {
        const playing = !video.paused;
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.setAttribute('aria-label', playing ? btn.dataset.labelPause || 'Pause video' : btn.dataset.labelPlay || 'Play video');
        btn.classList.toggle('is-paused', !playing);
      };
      btn.addEventListener('click', () => { if (video.paused) video.play(); else video.pause(); });
      video.addEventListener('play', sync); video.addEventListener('pause', sync);
      sync();
    });
  };

  const init = (root = document) => { revealAll(root); carousels(root); videos(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
